import { useEffect, useId, useRef, useState, type KeyboardEvent, type PointerEvent } from "react";

import { SERIES_COLORS, niceScale } from "./chart-scale";
import { formatNumber } from "./format";

export interface ChartSeries {
  key: string;
  label: string;
  values: number[];
}

export interface LineChartProps {
  /** Accessible name, e.g. "Messages per day". */
  title: string;
  /** One label per x position (already formatted). */
  labels: string[];
  series: ChartSeries[];
  height?: number;
}

const PAD = { top: 12, right: 16, bottom: 28, left: 52 };

function useWidth(fallback: number) {
  const ref = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(fallback);
  useEffect(() => {
    const node = ref.current;
    if (!node || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(([entry]) => {
      if (entry) setWidth(Math.max(240, Math.round(entry.contentRect.width)));
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);
  return [ref, width] as const;
}

/**
 * A single-axis line chart: 2px lines, hairline grid, crosshair + tooltip that
 * lists every series at the hovered point (arrow keys work too), legend for 2+ series.
 */
export function LineChart({ title, labels, series, height = 240 }: LineChartProps) {
  const [ref, width] = useWidth(640);
  const [active, setActive] = useState<number | null>(null);
  const titleId = useId();
  const count = labels.length;
  const plotW = width - PAD.left - PAD.right;
  const plotH = height - PAD.top - PAD.bottom;
  const max = Math.max(0, ...series.flatMap((s) => s.values));
  const { top, step } = niceScale(max);
  const x = (i: number) => PAD.left + (count <= 1 ? plotW / 2 : (i / (count - 1)) * plotW);
  const y = (v: number) => PAD.top + plotH - (v / top) * plotH;
  const ticks = Array.from({ length: Math.round(top / step) + 1 }, (_, i) => i * step);
  const every = Math.max(1, Math.ceil(count / Math.max(2, Math.floor(plotW / 90))));

  function pick(event: PointerEvent<SVGSVGElement>) {
    const box = event.currentTarget.getBoundingClientRect();
    const px = ((event.clientX - box.left) / box.width) * width;
    const index = count <= 1 ? 0 : Math.round(((px - PAD.left) / plotW) * (count - 1));
    setActive(Math.min(count - 1, Math.max(0, index)));
  }

  function onKey(event: KeyboardEvent<SVGSVGElement>) {
    if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
    event.preventDefault();
    const delta = event.key === "ArrowLeft" ? -1 : 1;
    setActive((current) => Math.min(count - 1, Math.max(0, (current ?? (delta > 0 ? -1 : count)) + delta)));
  }

  const tipLeft = active === null ? 0 : x(active);
  return (
    <figure className="m-0">
      {series.length > 1 && (
        <ul className="mb-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-600" aria-label="Legend">
          {series.map((s, i) => (
            <li key={s.key} className="flex items-center gap-1.5">
              <span aria-hidden className="inline-block h-0.5 w-4 rounded" style={{ background: SERIES_COLORS[i] }} />
              {s.label}
            </li>
          ))}
        </ul>
      )}
      <div ref={ref} className="relative">
        <svg
          width="100%"
          viewBox={`0 0 ${width} ${height}`}
          role="img"
          aria-labelledby={titleId}
          tabIndex={0}
          className="block touch-none select-none focus-visible:outline-2"
          onPointerMove={pick}
          onPointerLeave={() => setActive(null)}
          onFocus={() => setActive((current) => current ?? count - 1)}
          onBlur={() => setActive(null)}
          onKeyDown={onKey}
        >
          <title id={titleId}>{title}</title>
          {ticks.map((t) => (
            <g key={t}>
              <line x1={PAD.left} x2={width - PAD.right} y1={y(t)} y2={y(t)} stroke={t === 0 ? "#c3c2b7" : "#e1e0d9"} strokeWidth={1} />
              <text x={PAD.left - 8} y={y(t)} dy="0.32em" textAnchor="end" className="fill-slate-500 text-[11px] tabular-nums">
                {formatNumber(t)}
              </text>
            </g>
          ))}
          {labels.map((label, i) =>
            i % every === 0 ? (
              <text key={i} x={x(i)} y={height - 8} textAnchor="middle" className="fill-slate-500 text-[11px]">
                {label}
              </text>
            ) : null,
          )}
          {active !== null && (
            <line x1={x(active)} x2={x(active)} y1={PAD.top} y2={PAD.top + plotH} stroke="#898781" strokeWidth={1} />
          )}
          {series.map((s, si) => (
            <g key={s.key}>
              <polyline
                fill="none"
                stroke={SERIES_COLORS[si]}
                strokeWidth={2}
                strokeLinejoin="round"
                strokeLinecap="round"
                points={s.values.map((v, i) => `${x(i)},${y(v)}`).join(" ")}
              />
              {(count === 1 || active !== null) && (
                <circle
                  cx={x(active ?? 0)}
                  cy={y(s.values[active ?? 0] ?? 0)}
                  r={4}
                  fill={SERIES_COLORS[si]}
                  stroke="#ffffff"
                  strokeWidth={2}
                />
              )}
            </g>
          ))}
        </svg>
        {active !== null && (
          <div
            role="status"
            className="pointer-events-none absolute top-2 z-10 min-w-36 rounded-md border border-slate-200 bg-white px-3 py-2 text-xs shadow-md"
            style={
              tipLeft > width / 2
                ? { right: `${((width - tipLeft + 12) / width) * 100}%` }
                : { left: `${((tipLeft + 12) / width) * 100}%` }
            }
          >
            <p className="mb-1 text-slate-500">{labels[active]}</p>
            {series.map((s, si) => (
              <p key={s.key} className="flex items-center gap-2">
                <span aria-hidden className="inline-block h-0.5 w-3 rounded" style={{ background: SERIES_COLORS[si] }} />
                <span className="font-semibold text-slate-900 tabular-nums">{formatNumber(s.values[active] ?? 0)}</span>
                <span className="text-slate-500">{s.label}</span>
              </p>
            ))}
          </div>
        )}
      </div>
    </figure>
  );
}
